<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useMyOrganizations } from '@ilteducation/my-account';
import { HeroSelect } from '@ilteducation/components';

const { lgAndUp: isDesktop } = useDisplay();
const { isLoading, organizations, loadMyOrganizations } = useMyOrganizations(false);

type Props = {
  label: string;
  smallMode: boolean;
  rounded?: string | boolean;
};
withDefaults(defineProps<Props>(), { smallMode: false });

const model = defineModel<string>();
const emit = defineEmits(['update:model-value', 'not-found']);
onMounted(() => {
  if (!organizations.value.data.length) {
    loadMyOrganizations().finally(() => {
      checkCurrentOrg(model.value);
    });
  } else {
    checkCurrentOrg(model.value);
  }
});

const userOrganizations = computed(() => {
  return organizations.value.data
    .filter((o) => o.active)
    .filter((o) => !o.userManagementDisabled)
    .filter((o) => ['admin', 'employee'].includes(o.role));
});

const onModelChange = (value: unknown) => {
  emit('update:model-value', value);
};

watch(userOrganizations, () => {
  if (userOrganizations.value.length > 0 && !model.value) {
    model.value = userOrganizations.value[0].uid;
  }
  return model.value ?? null;
});

function checkCurrentOrg(orgUid: string | undefined | null) {
  if (!orgUid) {
    const firstOrg = userOrganizations.value[0];
    if (firstOrg) {
      model.value = firstOrg.uid;
    }
    return;
  }

  const org = userOrganizations.value.find((o) => o.uid === orgUid);
  if (!org) {
    const firstOrg = userOrganizations.value[0];
    if (firstOrg) {
      model.value = firstOrg.uid;
    }
    emit('not-found');
  }
}
</script>

<template>
  <span>
    <HeroSelect
      v-on="$attrs"
      :loading="isLoading"
      :items="userOrganizations"
      v-model="model"
      item-title="name"
      :width="smallMode ? 96 : undefined"
      item-value="uid"
      :label="label"
      :aria-label="label"
      :prepend-inner-icon="isDesktop ? 'mdi-home-city' : ''"
      hide-details
      single-line
      :rounded="rounded"
      :class="userOrganizations.length < 2 ? 'pointer-events-none' : ''"
      :menu-icon="userOrganizations.length > 1 ? 'mdi-menu-down' : ''"
      @update:model-value="onModelChange"
    >
    </HeroSelect>
  </span>
</template>

<style scoped lang="scss">
.pointer-events-none {
  pointer-events: none;
}
</style>
